import { Atom, useAtomSet, useAtomValue } from "@effect-atom/atom-react";
import { Link } from "@tanstack/react-router";
import { clawInstallIdentityAtom } from "~/lib/atoms/brand-identity.ts";
import { ClawInstallThemeToggle } from "./ClawInstallThemeToggle.tsx";

const mobileMenuOpenAtom = Atom.make(false);

/**
 * "Log In" control. Stays a router {@link Link} to `/clawinstall/login` when the
 * identity's auth surface is on this host (`authOrigin === null`); otherwise
 * links to the auth origin's `/login` (Consinna's auth lives on clawinstall.ai
 * in phase 1).
 */
function LoginLink({ authOrigin, className, onClick }: {
  authOrigin: string | null;
  className: string;
  onClick?: () => void;
}) {
  if (authOrigin === null) {
    return (
      <Link to="/clawinstall/login" className={className} onClick={onClick}>
        Log In
      </Link>
    );
  }
  return (
    <a href={`${authOrigin}/login`} className={className} onClick={onClick}>
      Log In
    </a>
  );
}

const navLinks = [
  { to: "/clawinstall", label: "Home" },
  { to: "/clawinstall/pricing", label: "Pricing" },
  { to: "/clawinstall/compare", label: "Compare" },
  { to: "/clawinstall/faq", label: "FAQ" },
  { to: "/clawinstall/security", label: "Security" },
] as const;

/**
 * ClawInstall site header.
 *
 * Sticky navigation for the ClawInstall marketing pages, themed via the
 * per-property tokens (light + dark) with a theme toggle and a mobile menu.
 *
 * @since 1.0.0
 */
export function ClawInstallHeader() {
  const mobileMenuOpen = useAtomValue(mobileMenuOpenAtom);
  const setMobileMenuOpen = useAtomSet(mobileMenuOpenAtom);
  const identity = useAtomValue(clawInstallIdentityAtom);
  const closeMenu = () => setMobileMenuOpen(false);

  return (
    <header className="sticky top-0 z-50 border-b border-outline-variant/60 bg-surface/80 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-[1200px] items-center justify-between px-6 md:px-20">
        <Link
          to="/clawinstall"
          className="flex items-center gap-2 font-headline-md text-headline-md font-semibold text-on-surface"
        >
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-container text-white">
            <span className="material-symbols-outlined !text-xl">cloud_done</span>
          </span>
          {identity.displayName}
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-7 md:flex">
          {navLinks.map((link) => (
            <Link
              key={link.label}
              to={link.to}
              className="font-label-md text-label-md text-on-surface-variant transition-colors hover:text-on-surface"
            >
              {link.label}
            </Link>
          ))}
          <ClawInstallThemeToggle />
          <LoginLink
            authOrigin={identity.authOrigin}
            className="font-label-md text-label-md text-on-surface-variant transition-colors hover:text-on-surface"
          />
          <Link
            to="/clawinstall/pricing"
            className="rounded-md bg-primary-container px-5 py-2.5 font-label-md text-label-md text-on-primary-container transition-colors hover:bg-primary"
            data-testid="cta-get-started"
          >
            Get Started
          </Link>
        </nav>

        {/* Mobile controls */}
        <div className="flex items-center gap-2 md:hidden">
          <ClawInstallThemeToggle />
          <button
            type="button"
            className="rounded-md p-2 text-on-surface-variant hover:bg-surface-variant hover:text-on-surface"
            aria-label="Toggle menu"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              {mobileMenuOpen ?
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /> :
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />}
            </svg>
          </button>
        </div>
      </div>

      {
        /* Mobile menu — CSS mount animation (framer-motion removed to keep the
          motion vendor chunk off the marketing landing critical graph). */
      }
      {mobileMenuOpen ?
        (
          <div className="animate-menu-in overflow-hidden border-t border-outline-variant/60 bg-surface md:hidden">
            <nav className="flex flex-col gap-1 px-6 py-4">
              {navLinks.map((link) => (
                <Link
                  key={link.label}
                  to={link.to}
                  className="rounded-md px-3 py-2 font-label-md text-label-md text-on-surface-variant hover:bg-surface-variant hover:text-on-surface"
                  onClick={closeMenu}
                >
                  {link.label}
                </Link>
              ))}
              <LoginLink
                authOrigin={identity.authOrigin}
                className="rounded-md px-3 py-2 font-label-md text-label-md text-on-surface-variant hover:bg-surface-variant hover:text-on-surface"
                onClick={closeMenu}
              />
              <Link
                to="/clawinstall/pricing"
                className="mt-2 rounded-md bg-primary-container px-3 py-2 text-center font-label-md text-label-md text-on-primary-container hover:bg-primary"
                onClick={closeMenu}
                data-testid="cta-get-started-mobile"
              >
                Get Started
              </Link>
            </nav>
          </div>
        ) :
        null}
    </header>
  );
}
